import { useCallback, useEffect, useRef, useState } from 'react';
import { supportWhatsApp } from '../../../../../config';
import { studioApi, studioMock, idempotencyKey, errorKey } from '../../../data/studioApi';

const PER_PAGE = 10;
// Statuses that keep the plan (core `isEntitled`): cancelling or reactivating needs one.
const ENTITLED = ['trialing', 'active', 'past_due'];
const EMPTY = { status: 'idle', list: [], page: 0, lastPage: 1 };

// /studio/plan: the subscription (`GET /subscription`, through `studio.payments`), the
// balance (`GET /usage`) and the invoices (`GET /invoices`, newest first). Cancelling is not
// self-service: it leads to the support WhatsApp with the plan and the account in the
// message, and support cancels in Stripe. A cancellation scheduled there can be undone here
// (`PATCH /subscription { cancel_at_period_end: false }`). The card is changed in Stripe's
// portal (`POST /payment-portal-sessions`), which comes back here. Upgrades and credit packs
// open the plan dialog.
export default function usePlanPage(s) {
  const { auth, features, payments: p } = s;
  const [invoices, setInvoices] = useState(EMPTY);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  // What the page can show: sign-in, the profile to connect first, payments off, a failed
  // first read, or the plan. Anything else is still loading.
  const creator = s.snapshot.creator;
  const ready = s.accountStatus === 'ready';
  const needsProfile = auth.signed && ready && (!creator || creator.onboarding_status === 'pending');
  const unavailable = auth.signed && !auth.loading && (['unavailable', 'error'].includes(s.accountStatus) || (ready && !needsProfile && !features.payments));
  const failed = !unavailable && !needsProfile && p.status === 'error';
  const loading = auth.signed ? !(needsProfile || unavailable || failed || p.enabled) : auth.loading;
  const subscription = p.subscription;
  const live = !!subscription?.id;
  const entitled = live && ENTITLED.includes(subscription.status);

  const loadInvoices = useCallback(async (page = 1) => {
    setInvoices(current => ({ ...current, status: page === 1 ? 'loading' : 'more' }));
    try {
      const result = await studioApi.invoices({ page, per_page: PER_PAGE });
      if (!alive.current) return;
      setInvoices(current => {
        // A newer invoice shifts the pages: drop what the list already holds.
        const known = page === 1 ? [] : current.list;
        const fresh = result.data.filter(item => !known.some(row => row.id === item.id));
        return { status: 'ready', list: [...known, ...fresh], page: result.current_page, lastPage: result.last_page };
      });
    } catch {
      if (alive.current) setInvoices(current => ({ ...current, status: page === 1 ? 'error' : 'ready' }));
    }
  }, []);

  // Coming back from the card portal, an email or another tab: plan and balance read again.
  useEffect(() => { if (p.enabled) p.refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (p.enabled) loadInvoices(1); else setInvoices(EMPTY); }, [p.enabled, loadInvoices]);
  // A purchase or an upgrade in the plan dialog bills an invoice: the list follows it.
  const planOpen = s.modal?.type === 'plan';
  const wasOpen = useRef(planOpen);
  useEffect(() => {
    if (wasOpen.current && !planOpen && p.enabled) loadInvoices(1);
    wasOpen.current = planOpen;
  }, [planOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (name, task) => {
    setBusy(name);
    setError(null);
    try { return await task(); } catch (failure) { if (alive.current) setError(errorKey(failure)); return null; } finally { if (alive.current) setBusy(null); }
  };

  // Support cancels in Stripe; the message says which plan and which account.
  const contactSupport = () => {
    const user = auth.user || {};
    const handle = s.accounts?.active?.handle;
    const account = user.email?.address || (handle ? `@${handle}` : user.username) || '';
    const message = s.t('billingCancelSupportMessage', { plan: s.t(`planName_${p.plan}`), account });
    window.open(`https://wa.me/${supportWhatsApp.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    setConfirming(false);
  };

  // A cancellation scheduled at the end of the cycle is undone before then.
  const resume = () => run('resume', async () => {
    const next = await studioApi.updateSubscription({ cancel_at_period_end: false }, idempotencyKey('subscription'));
    if (alive.current) p.setSubscription(next);
  });

  // The portal returns to this page. The mock's portal is not a real page: a new tab.
  const openPortal = () => run('portal', async () => {
    const session = await studioApi.createPortalSession(idempotencyKey('portal'));
    if (studioMock) window.open(session.url, '_blank', 'noopener,noreferrer');
    else window.location.assign(session.url);
  });

  // After paying a held upgrade (or an open renewal) on Stripe's page.
  const recheck = () => run('recheck', async () => {
    await p.refresh({ planChanged: true });
    if (alive.current) await loadInvoices(1);
  });

  const openUrl = url => { if (url) window.open(url, '_blank', 'noopener,noreferrer'); };
  // The renewal Stripe could not charge, to pay with another card on its page.
  const openInvoice = invoices.list.find(item => item.status === 'open' && item.hosted_invoice_url) || null;

  return {
    loading, needsProfile, unavailable, failed, retry: p.retry,
    subscription, live, entitled, canUpgrade: subscription?.status === 'active' && !subscription?.pending_change && !!p.nextPlan,
    invoices, openInvoice, loadMore: () => loadInvoices(invoices.page + 1), reloadInvoices: () => loadInvoices(1),
    busy, error, clearError: () => setError(null),
    confirming, askCancel: () => { setError(null); setConfirming(true); }, closeCancel: () => setConfirming(false),
    contactSupport, supportPhone: supportWhatsApp, resume, openPortal, recheck, openUrl,
  };
}
